import React, {useCallback, useState, useEffect} from 'react';
import {Select} from 'antd';
import {SyncOutlined} from '@ant-design/icons';
import config from "../config";
import {useDebounce} from "rooks";
import useAllStops from '../hooks/useAllStops';
import DisplayStop from './DisplayStop';
import { snapToRoad, snapToRoadHere } from '../libs/pathLib';

const LocationLookup = ({location, setLocation, limit = 5, allStops, type, ...props}) => {
    const [options, setOptions] = useState(location? [location] : []);
    const [searchValue, setSearchValue] = useState('');
    const [loading, setLoading] = useState(false);

    // Create a debounced search function
    const debouncedSearch = useDebounce(async (value) => {
        if (!value) {
            setOptions(location? [location] : []);
            setLoading(false);
            return;
        }

        setLoading(true);
        // Immediately show local stops
        let localOptions = [];
        if (allStops) {
            const searchLower = value.toLowerCase();
            const matchingStops = Object.values(allStops)
                .filter((stop) => stop.duplicate === -1 && stop?.stopName?.toLowerCase().includes(searchLower))
                .slice(0, limit)
                .map((stop) => ({
                    value: `${stop.stopName}|${stop.lat}|${stop.lon}`,
                    label: <DisplayStop stop={stop} noWarning={true} noCode={true} noRoutes={true} noInterchange={true} showOpenInNewTab={true} />,
                    coordinates: [stop.lon, stop.lat],
                    address: stop.stopName,
                    type: 'stop',
                    ...stop
                }));
            
            localOptions = matchingStops;
            // Set options immediately with local results
            setOptions(localOptions);
        }

        try {
            let apiOptions = [];
            
            if (type === 'google') {
                // Use Google Places API
                const response = await fetch(`https://places.googleapis.com/v1/places:searchText`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Goog-Api-Key': config.maps.google,
                        'X-Goog-FieldMask': 'places.displayName,places.formattedAddress,places.location,places.id,places.types,places.addressComponents'
                    },
                    body: JSON.stringify({
                        textQuery: value,
                        regionCode: 'AU',
                        maxResultCount: limit
                    })
                });
                
                const googleData = await response.json();

                if (googleData.places) {
                    apiOptions = googleData.places.map((place) => {
                        const address = place.formattedAddress;
                        const lat = place.location.latitude;
                        const lon = place.location.longitude;
                        const coordinates = `${lat}|${lon}`;
                        const value = `${address}|${coordinates}`;
                        
                        // Determine stopName based on place types
                        let stopName;
                        const placeTypes = place.types || [];
                        if (placeTypes.includes('street_address')) {
                            stopName = place.formattedAddress;
                        } else if (placeTypes.includes('point_of_interest')) {
                            stopName = `${place.displayName.text}, ${place.formattedAddress}`;
                        } else {
                            stopName = place.displayName.text || place.formattedAddress;
                        }
                        
                        // Extract additional address components
                        const addressComponents = place.addressComponents || [];
                        const streetNumber = addressComponents.find(comp => comp.types?.includes('street_number'))?.longText || '';
                        const route = addressComponents.find(comp => comp.types?.includes('route'))?.longText || '';
                        const locality = addressComponents.find(comp => comp.types?.includes('locality'))?.longText || '';
                        const state = addressComponents.find(comp => comp.types?.includes('administrative_area_level_1'))?.longText || '';
                        const postcode = addressComponents.find(comp => comp.types?.includes('postal_code'))?.longText || '';
                        const country = addressComponents.find(comp => comp.types?.includes('country'))?.longText || '';
                        
                        // Determine label based on place types
                        let label;
                        if (placeTypes.includes('street_address')) {
                            label = address;
                        } else if (placeTypes.includes('point_of_interest')) {
                            label = `${place.displayName.text}, ${address}`;
                        } else {
                            label = address;
                        }
                        
                        return {
                            value,
                            label: label,
                            coordinates: [lon, lat],
                            name: place.displayName.text || address,
                            stopName,
                            address: address,
                            type: 'address',
                            place_id: place.id || place.placeId,
                            lat,
                            lon,
                            // Additional address components for consistency with Geoapify
                            street: route,
                            streetNumber,
                            city: locality,
                            state,
                            postcode,
                            country,
                            types: place.types || [],
                            addressComponents
                        };
                    });
                }
            } else {
                // Use Geoapify API (default)
                const response = await fetch(`https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(value)}&apiKey=${config.maps.geoapify.ctrxs}&filter=countrycode:au&limit=${limit}`);
                const geoapifyData = await response.json();

                if (geoapifyData.features) {
                    apiOptions = geoapifyData.features.map((feature) => {
                        const address = feature.properties.formatted;
                        const coordinates = `${feature.properties.lat}|${feature.properties.lon}`;
                        const value = `${address}|${coordinates}`;
                        const stopName = feature.properties.address_line1 || feature.properties.name;
                        
                        const label = address;
                        
                        return {
                            value,
                            label,
                            coordinates: [feature.properties.lon, feature.properties.lat],
                            name: feature.properties.name || address,
                            stopName,
                            address: address,
                            type: 'address',
                            place_id: feature.properties.place_id,
                            ...feature.properties
                        };
                    });
                }
            }
            
            // Combine local and API results (deduplicated)
            const combinedOptions = [...localOptions, ...apiOptions];
            const uniqueOptions = Array.from(
                new Map(
                    combinedOptions.map(opt => [opt.stopId || opt.place_id || opt.value, opt])
                ).values()
            );
            setOptions(uniqueOptions);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, 500);

    // Update search value when user types
    const handleSearch = useCallback(value => {
        setSearchValue(value);
        debouncedSearch(value);
    }, [debouncedSearch]);

    return (
        <Select
            showSearch
            suffixIcon={loading ? <SyncOutlined spin={true} style={{color: '#777'}}/> : undefined}
            placeholder="Search for a location, point of interest, or bus stop"
            defaultActiveFirstOption={false}
            filterOption={false}
            value={searchValue?.length ? searchValue : location?.value}
            onSearch={handleSearch}
            notFoundContent={null}
            options={options}
            onSelect={async (value, option) => {
                if (option.type === 'address') {
                    setLoading(true);
                    const { lat, lon } = await snapToRoadHere(option);
                    // Check lat/lon are valid numbers and within range
                    if (
                        isNaN(lat) ||
                        isNaN(lon) ||
                        lat < -90 || lat > 90 ||
                        lon < -180 || lon > 180
                    ) {
                        setLoading(false);
                        return;
                    }
                    option.coordinates = [lon, lat];
                    option.lat = lat;
                    option.lon = lon;
                    setLoading(false);
                }
                setSearchValue('');
                setOptions(prev => {
                    const key = option.stopId || option.place_id || option.value;
                    const filtered = prev.filter(opt => (opt.stopId || opt.place_id || opt.value) !== key);
                    return [option, ...filtered];
                });
                setLocation(value, option);
            }}
            {...props}
        />
    );
};

export default LocationLookup; 